// Pure helpers for the dependency-free visitor map: an equirectangular projection (Antarctica
// cropped), regional-indicator flag emojis, localized country names and a square-root bubble scale.

/** The map's SVG coordinate frame: full longitude, latitude cropped to +85°…−60° (no Antarctica). */
export const MAP_VIEW = { width: 360, height: 145, latTop: 85 } as const;

/**
 * Equirectangular projection into the map's SVG frame. Longitude −180…180 spans the full width;
 * latitude is measured down from the northern edge (latTop), so north is up and Antarctica falls
 * below the cropped frame.
 */
export function project(lon: number, lat: number): { x: number; y: number } {
  return { x: lon + 180, y: MAP_VIEW.latTop - lat };
}

const REGIONAL_INDICATOR_A = 0x1f1e6;

/** The flag emoji for an ISO 3166-1 alpha-2 code (e.g. "CD" → 🇨🇩); "" for anything that isn't two letters. */
export function flagEmoji(alpha2: string): string {
  const code = (alpha2 || "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return "";
  const a = REGIONAL_INDICATOR_A + (code.charCodeAt(0) - 65);
  const b = REGIONAL_INDICATOR_A + (code.charCodeAt(1) - 65);
  return String.fromCodePoint(a, b);
}

// One formatter per locale : Intl.DisplayNames construction isn't free; reuse it across rows.
const displayNamesCache = new Map<string, Intl.DisplayNames>();

/** Localized country name for an alpha-2 code (default French); falls back to the raw code. */
export function countryName(alpha2: string, locale = "fr"): string {
  const code = (alpha2 || "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return code;
  try {
    let dn = displayNamesCache.get(locale);
    if (!dn) {
      dn = new Intl.DisplayNames([locale], { type: "region", fallback: "code" });
      displayNamesCache.set(locale, dn);
    }
    return dn.of(code) ?? code;
  } catch {
    return code;
  }
}

/**
 * Bubble radius on a square-root scale, so a bubble's AREA is proportional to its request count
 * (the honest way to size circles). Zero traffic : or an empty dataset : gets the minimum radius.
 */
export function bubbleRadius(requests: number, maxRequests: number, minR = 4, maxR = 20): number {
  if (!(requests > 0) || !(maxRequests > 0)) return minR;
  const ratio = Math.min(1, requests / maxRequests);
  return minR + (maxR - minR) * Math.sqrt(ratio);
}
